/**
 * Game state machine store.
 *
 * States: main-menu | loading | story | input-freeform | input-guided | overlay | idle-waiting
 */

type GameState =
  | "main-menu"
  | "loading"
  | "world-creation"
  | "character-creation"
  | "opening-scene"
  | "story"
  | "input-freeform"
  | "input-guided"
  | "overlay"
  | "idle-waiting";

interface GameStore {
  state: GameState;
}

/** Valid state transitions. */
const TRANSITIONS: Record<GameState, GameState[]> = {
  "main-menu": ["loading", "world-creation"],
  loading: ["story", "main-menu"],
  "world-creation": ["character-creation", "main-menu"],
  "character-creation": ["opening-scene", "world-creation"],
  "opening-scene": ["story", "input-guided", "main-menu"],
  story: ["input-freeform", "input-guided", "overlay", "idle-waiting", "main-menu"],
  "input-freeform": ["story", "overlay", "main-menu"],
  "input-guided": ["story", "overlay", "main-menu"],
  overlay: ["story", "input-freeform", "input-guided", "main-menu"],
  "idle-waiting": ["story", "overlay", "main-menu"],
};

/** Create a reactive game state store with validated transitions. */
function createGameStore(): GameStore & {
  transition: (to: GameState) => boolean;
} {
  let state = $state<GameState>("main-menu");

  return {
    get state() {
      return state;
    },
    set state(value: GameState) {
      state = value;
    },
    transition(to: GameState): boolean {
      const allowed = TRANSITIONS[state];
      if (allowed.includes(to)) {
        state = to;
        return true;
      }
      console.warn(`Invalid transition: ${state} -> ${to}`);
      return false;
    },
  };
}

export const gameStore = createGameStore();
export type { GameState };

/* ------------------------------------------------------------------ */
/* World creation store                                                */
/* ------------------------------------------------------------------ */

function createWorldCreationStore() {
  let genre = $state("");
  let era = $state("");
  let tone = $state("");
  let freeformPrompt = $state("");
  let worldName = $state("");
  let worldSetting = $state("");

  return {
    get genre() {
      return genre;
    },
    set genre(v: string) {
      genre = v;
    },
    get era() {
      return era;
    },
    set era(v: string) {
      era = v;
    },
    get tone() {
      return tone;
    },
    set tone(v: string) {
      tone = v;
    },
    get freeformPrompt() {
      return freeformPrompt;
    },
    set freeformPrompt(v: string) {
      freeformPrompt = v;
    },
    get worldName() {
      return worldName;
    },
    set worldName(v: string) {
      worldName = v;
    },
    get worldSetting() {
      return worldSetting;
    },
    set worldSetting(v: string) {
      worldSetting = v;
    },
    /** Assemble a setting prompt from all fields. */
    get assembledPrompt(): string {
      const parts: string[] = [];
      if (genre) parts.push(`Genre: ${genre}`);
      if (era) parts.push(`Era: ${era}`);
      if (tone) parts.push(`Tone: ${tone}`);
      if (freeformPrompt) parts.push(freeformPrompt);
      return parts.join(". ");
    },
    reset(): void {
      genre = "";
      era = "";
      tone = "";
      freeformPrompt = "";
      worldName = "";
      worldSetting = "";
    },
  };
}

export const worldCreationStore = createWorldCreationStore();

/* ------------------------------------------------------------------ */
/* Overlay store                                                       */
/* ------------------------------------------------------------------ */

type OverlayKind = "map" | "timeline" | "relationships" | "journal" | "settings";

function createOverlayStore() {
  let active = $state<OverlayKind | null>(null);

  return {
    get active() {
      return active;
    },
    /** Toggle an overlay: if it is already open, close it; otherwise open it. */
    toggle(kind: OverlayKind): void {
      active = active === kind ? null : kind;
    },
    close(): void {
      active = null;
    },
  };
}

export const overlayStore = createOverlayStore();
export type { OverlayKind };

/* ------------------------------------------------------------------ */
/* Settings store                                                      */
/* ------------------------------------------------------------------ */

type InputMode = "freeform" | "guided";
type RulesMode = "strict" | "loose";
type AnimationLevel = "prominent" | "subtle" | "off";

interface Settings {
  wpm: number;
  inputMode: InputMode;
  rulesMode: RulesMode;
  animationLevel: AnimationLevel;
  glyphSpeedMs: number;
  glyphDensity: number;
}

function createSettingsStore() {
  let wpm = $state(350);
  let inputMode = $state<InputMode>("guided");
  let rulesMode = $state<RulesMode>("strict");
  let animationLevel = $state<AnimationLevel>("prominent");
  let glyphSpeedMs = $state(200);
  let glyphDensity = $state(20);

  return {
    get wpm() {
      return wpm;
    },
    set wpm(v: number) {
      wpm = Math.max(100, Math.min(600, v));
    },
    get inputMode() {
      return inputMode;
    },
    set inputMode(v: InputMode) {
      inputMode = v;
    },
    get rulesMode() {
      return rulesMode;
    },
    set rulesMode(v: RulesMode) {
      rulesMode = v;
    },
    get animationLevel() {
      return animationLevel;
    },
    set animationLevel(v: AnimationLevel) {
      animationLevel = v;
    },
    get glyphSpeedMs() {
      return glyphSpeedMs;
    },
    set glyphSpeedMs(v: number) {
      glyphSpeedMs = Math.max(100, Math.min(500, v));
    },
    get glyphDensity() {
      return glyphDensity;
    },
    set glyphDensity(v: number) {
      glyphDensity = Math.max(5, Math.min(50, v));
    },
    get snapshot(): Settings {
      return { wpm, inputMode, rulesMode, animationLevel, glyphSpeedMs, glyphDensity };
    },
    reset(): void {
      wpm = 350;
      inputMode = "guided";
      rulesMode = "strict";
      animationLevel = "prominent";
      glyphSpeedMs = 200;
      glyphDensity = 20;
    },
  };
}

export const settingsStore = createSettingsStore();
export type { InputMode, RulesMode, AnimationLevel, Settings };
